import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTab } from '../redux/slices/appSlice';
import './MobileBottomNav.scss';

// Bottom tab bar shown on mobile web + iOS + Android.
// Five primary destinations: For You · Mixpedia · Planner · Digger · Library.
//
// Icon shapes intentionally echo the existing category SVGs in
// assets/icons/categories/ so the visual language stays consistent.
// Inlined instead of imported so they stroke/fill with currentColor
// and hit both active/inactive states without CSS filter hacks.

const FeedIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="6" cy="18" r="2" fill="currentColor" />
        <path d="M5 11a8 8 0 0 1 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M5 5a14 14 0 0 1 14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Mixpedia — search/magnifying-glass on mobile: the tab is the primary
// search entry point, so a search glyph reads more clearly than the
// desktop crosshair. Matches assets/icons/search.svg.
const MixpediaIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

// Planner — node graph, mirroring whatsnext.svg
const PlannerIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="5" cy="12" r="2" />
        <circle cx="13" cy="6" r="2" />
        <circle cx="13" cy="18" r="2" />
        <circle cx="20" cy="12" r="1.8" />
        <path d="M7 12c2 0 3-6 4-6" />
        <path d="M7 12c2 0 3 6 4 6" />
        <path d="M15 6c2 0 3 6 3.5 6" />
        <path d="M15 18c2 0 3-6 3.5-6" />
    </svg>
);

// Digger — two stacked album covers with a magnifying glass. Mirrors
// assets/icons/categories/recommendations.svg used on desktop, so the
// nav icon and the desktop category icon are visually identical.
// Pulled inline because the SVG uses <mask> for the overlap effect
// and importing the file as an <img> wouldn't pick up currentColor
// for active/inactive states.
const DiggerIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <defs>
            <mask id="mbn-digger-topMask">
                <rect width="24" height="24" fill="white" />
                <rect x="3" y="7" width="11" height="11" rx="2" fill="black" />
            </mask>
            <mask id="mbn-digger-bottomMask">
                <rect width="24" height="24" fill="white" />
                <circle cx="6.5" cy="17.5" r="3.5" fill="black" />
            </mask>
        </defs>
        {/* top cover (behind) */}
        <rect x="7" y="3" width="11" height="11" rx="2" mask="url(#mbn-digger-topMask)" />
        {/* bottom cover (front) */}
        <rect x="3" y="7" width="11" height="11" rx="2" mask="url(#mbn-digger-bottomMask)" />
        {/* magnifying glass */}
        <circle cx="6.5" cy="17.5" r="3" />
        <line x1="8.6" y1="19.6" x2="10.8" y2="21.8" strokeLinecap="round" />
    </svg>
);

// Library — stacked books. Clean, readable at 26px, matches
// Spotify/Apple Music "your library" conventions.
const LibraryIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
);

// Library tab now covers Likes / Favourites / Playlists only. Digger
// has its own dedicated tab so it doesn't need to be grouped here.
const LIBRARY_KEYS = new Set(['library', 'likes', 'followed-artists', 'playlists']);

const TABS = [
    { key: 'feed',       label: 'For You',  Icon: FeedIcon },
    { key: 'djpal',      label: 'Mixpedia', Icon: MixpediaIcon },
    { key: 'whats-next', label: 'Planner',  Icon: PlannerIcon },
    { key: 'smart-recs', label: 'Digger',   Icon: DiggerIcon },
    { key: 'library',    label: 'Library',  Icon: LibraryIcon, matches: LIBRARY_KEYS },
];

const MobileBottomNav = () => {
    const dispatch = useDispatch();
    const activeTab = useSelector((state) => state.app.activeTab);
    // Where the Library item returns to: the last Library-section page the
    // user was on (likes / favourites / playlists), not the generic landing.
    const lastLibraryTab = useSelector((state) => state.app.lastLibraryTab);

    return (
        <nav className="mobile-bottom-nav" role="tablist" aria-label="Primary">
            {TABS.map(({ key, label, Icon, matches }) => {
                const active = matches
                    ? matches.has(activeTab)
                    : activeTab === key || (!activeTab && key === 'feed');
                return (
                    <button
                        key={key}
                        role="tab"
                        aria-selected={active}
                        className={`mobile-bottom-nav-item${active ? ' active' : ''}`}
                        onClick={() => {
                            // Tapping the already-active tab scrolls it to the top
                            // (standard mobile behavior) instead of being a no-op.
                            if (active) {
                                // Library re-tap while on a sub-page pops to the
                                // landing (same convention as Mixpedia re-tap →
                                // home); re-tap on the landing scrolls to top.
                                if (key === 'library' && activeTab !== 'library') {
                                    dispatch(setActiveTab('library'));
                                    return;
                                }
                                window.dispatchEvent(new CustomEvent('mixgoat:tab-retap', { detail: { key } }));
                            } else if (key === 'library') {
                                // Entering the Library section resumes the page
                                // the user was on, not the generic landing.
                                dispatch(setActiveTab(lastLibraryTab || 'library'));
                            } else {
                                dispatch(setActiveTab(key));
                            }
                        }}
                    >
                        <Icon />
                        <span className="mobile-bottom-nav-label">{label}</span>
                    </button>
                );
            })}
        </nav>
    );
};

export default MobileBottomNav;
